/**
 * Breadcrumb context — automatic navigation history tracking.
 *
 * How it works:
 *  - BreadcrumbProvider listens to location changes and maintains a stack.
 *  - Pages with dynamic titles call `useBreadcrumbTitle(name)` (one line).
 *  - The stack is rebuilt using:
 *      1. Already-in-stack  → pop back to that entry (handles browser back)
 *      2. Root-level page   → reset (new section)
 *      3. Same section + same depth → replace last (sibling nav)
 *      4. Otherwise          → push (going deeper)
 *
 * Route config defines logical depth and section grouping so that
 * e.g. /workspaces/:wsSlug/departments/marketing/content/source/:id
 * are treated as depth-2 and depth-3 within the same section.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { matchPath } from "react-router-dom";
import { getDepartmentGroup, getTeamConfig } from "@/config/departments";

// ─── Route config ────────────────────────────────────────────────────────────

interface RouteConfig {
  pattern: string;
  label: string; // static label; '' means dynamic (set via useBreadcrumbTitle)
  section: string;
  depth: number; // logical depth (1 = root section page)
  /** Resolve label from URL params for synthesized intermediate entries. */
  resolveLabel?: (params: Record<string, string>) => string;
}

const ROUTE_CONFIG: RouteConfig[] = [
  // Workspaces
  { pattern: "/workspaces", label: "Workspaces", section: "workspaces", depth: 1 },
  { pattern: "/workspaces/:id", label: "", section: "workspaces", depth: 2 },
  // Artifacts (detail only — list is now inside workspaces)
  { pattern: "/artifacts/:id", label: "", section: "workspaces", depth: 2 },
  // Brands
  { pattern: "/brands", label: "Brands", section: "brands", depth: 1 },
  { pattern: "/brands/:id", label: "", section: "brands", depth: 2 },
  { pattern: "/brands/:id/deck", label: "Brand Deck", section: "brands", depth: 3 },
  // Departments
  { pattern: "/departments", label: "Departments", section: "departments", depth: 1 },
  { pattern: "/departments/:dept", label: "", section: "departments", depth: 2, resolveLabel: (p) => getDepartmentGroup(p.dept)?.label ?? p.dept },
  { pattern: "/departments/:dept/:team", label: "", section: "departments", depth: 3, resolveLabel: (p) => getTeamConfig(p.dept, p.team)?.label ?? p.team },
  { pattern: "/departments/marketing/content/source/:id", label: "", section: "departments", depth: 4 },
  { pattern: "/departments/marketing/content/article/:id", label: "", section: "departments", depth: 4 },
  // Workspace-scoped departments
  { pattern: "/workspaces/:ws/departments", label: "Departments", section: "ws-departments", depth: 1 },
  { pattern: "/workspaces/:ws/departments/:dept", label: "", section: "ws-departments", depth: 2, resolveLabel: (p) => getDepartmentGroup(p.dept)?.label ?? p.dept },
  { pattern: "/workspaces/:ws/departments/:dept/:team", label: "", section: "ws-departments", depth: 3, resolveLabel: (p) => getTeamConfig(p.dept, p.team)?.label ?? p.team },
  { pattern: "/workspaces/:ws/departments/marketing/content/source/:id", label: "", section: "ws-departments", depth: 4 },
  { pattern: "/workspaces/:ws/departments/marketing/content/article/:id", label: "", section: "ws-departments", depth: 4 },
  // Legacy coagent (still needed for redirects to resolve breadcrumbs)
  { pattern: "/coagent", label: "Departments", section: "departments", depth: 1 },
  { pattern: "/coagent/:slug", label: "", section: "departments", depth: 2 },
  // Settings
  { pattern: "/settings", label: "Settings", section: "settings", depth: 1 },
  { pattern: "/settings/:page", label: "", section: "settings", depth: 2 },
  { pattern: "/settings/admin", label: "Admin", section: "settings", depth: 2 },
  { pattern: "/settings/admin/:page", label: "", section: "settings", depth: 3 },
  { pattern: "/settings/agent-logs", label: "Agent Logs", section: "settings", depth: 2 },
  { pattern: "/settings/agent-logs/:id", label: "", section: "settings", depth: 3 },
  // Agent Console (Phase 3.5) — separate "console" section, not nested under Settings
  { pattern: "/console", label: "Agent console", section: "console", depth: 1 },
  { pattern: "/console/agents", label: "Agents", section: "console", depth: 2 },
  { pattern: "/console/playground", label: "Playground", section: "console", depth: 2 },
  { pattern: "/console/playground/:agentName", label: "", section: "console", depth: 3 },
  { pattern: "/console/sessions", label: "Sessions", section: "console", depth: 2 },
  { pattern: "/console/sessions/:id", label: "", section: "console", depth: 3 },
];

function matchRoute(pathname: string): RouteConfig | null {
  // Match longest (most specific) pattern first
  const sorted = [...ROUTE_CONFIG].sort((a, b) => b.pattern.length - a.pattern.length);
  for (const cfg of sorted) {
    if (matchPath({ path: cfg.pattern, end: true }, pathname)) return cfg;
  }
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BreadcrumbEntry {
  pathname: string;
  search: string; // query string, e.g. "?ws=abc" — preserved on back-navigation
  label: string;
  section: string;
  depth: number;
}

interface BreadcrumbContextValue {
  stack: BreadcrumbEntry[];
  setCurrentTitle: (title: string, forPathname: string) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  stack: [],
  setCurrentTitle: () => {},
});

// ─── Provider ────────────────────────────────────────────────────────────────

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const stackRef = useRef<BreadcrumbEntry[]>([]);
  const [stack, setStack] = useState<BreadcrumbEntry[]>([]);
  const pendingTitleRef = useRef<{ pathname: string; title: string } | null>(null);

  const applyStack = useCallback((next: BreadcrumbEntry[]) => {
    stackRef.current = next;
    setStack(next);
  }, []);

  useEffect(() => {
    const cfg = matchRoute(location.pathname);
    const entry: BreadcrumbEntry = {
      pathname: location.pathname,
      search: location.search,
      label: cfg?.label ?? "",
      section: cfg?.section ?? location.pathname,
      depth: cfg?.depth ?? 1,
    };

    const prev = stackRef.current;

    // 1. Already in stack (same pathname) — pop back to it and update search
    const existingIdx = prev.findIndex((e) => e.pathname === location.pathname);
    if (existingIdx >= 0) {
      const updated = [
        ...prev.slice(0, existingIdx),
        { ...prev[existingIdx], search: location.search },
      ];
      applyStack(updated);
      return;
    }

    // 2. Root page — start fresh
    if (entry.depth === 1) {
      applyStack([entry]);
      return;
    }

    // 3. Same section + same depth — sibling navigation, replace last
    if (prev.length > 0) {
      const last = prev[prev.length - 1];
      if (last.section === entry.section && last.depth === entry.depth) {
        applyStack([...prev.slice(0, -1), entry]);
        return;
      }
    }

    // 4. Push deeper — synthesize intermediate entries for depth gaps
    const lastDepth = prev.length ? prev[prev.length - 1].depth : 0;
    const intermediates: BreadcrumbEntry[] = [];

    if (entry.depth - lastDepth > 1) {
      const segments = entry.pathname.split("/").filter(Boolean);
      for (let i = segments.length - 1; i > 0; i--) {
        const parentPath = "/" + segments.slice(0, i).join("/");
        if (prev.some((e) => e.pathname === parentPath)) continue;
        const parentCfg = matchRoute(parentPath);
        if (parentCfg && parentCfg.depth > lastDepth && parentCfg.depth < entry.depth) {
          const params = matchPath({ path: parentCfg.pattern, end: true }, parentPath)?.params ?? {};
          intermediates.push({
            pathname: parentPath,
            search: "",
            label: parentCfg.resolveLabel?.(params as Record<string, string>) ?? parentCfg.label,
            section: parentCfg.section,
            depth: parentCfg.depth,
          });
        }
      }
      intermediates.sort((a, b) => a.depth - b.depth);
    }

    let next = [...prev, ...intermediates, entry];

    // Apply any pending title that arrived before this entry was pushed
    const pending = pendingTitleRef.current;
    if (pending && pending.pathname === entry.pathname) {
      next = next.map((e) =>
        e.pathname === pending.pathname ? { ...e, label: pending.title } : e,
      );
      pendingTitleRef.current = null;
    }

    applyStack(next);
  }, [location.key, location.pathname, applyStack]);

  const setCurrentTitle = useCallback((title: string, forPathname: string) => {
    const current = stackRef.current;
    const idx = current.findIndex((e) => e.pathname === forPathname);
    if (idx === -1) {
      // Entry not yet pushed (child effect fired before parent's location effect)
      pendingTitleRef.current = { pathname: forPathname, title };
      return;
    }
    if (current[idx].label === title) return;
    const updated = current.map((e, i) =>
      i === idx ? { ...e, label: title } : e,
    );
    stackRef.current = updated;
    setStack(updated);
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ stack, setCurrentTitle }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Pages with dynamic titles call this hook to register their name.
 * Example: useBreadcrumbTitle(workspace?.name)
 */
export function useBreadcrumbTitle(title: string | null | undefined) {
  const { setCurrentTitle } = useContext(BreadcrumbContext);
  const { pathname } = useLocation();
  useEffect(() => {
    if (title) setCurrentTitle(title, pathname);
  }, [title, setCurrentTitle, pathname]);
}

export function useBreadcrumbs(): BreadcrumbEntry[] {
  return useContext(BreadcrumbContext).stack;
}

/**
 * Returns a callback that navigates to the previous breadcrumb entry.
 * Falls back to `fallback` path when there is no history in the stack.
 */
export function useBreadcrumbBack(fallback: string): () => void {
  const { stack } = useContext(BreadcrumbContext);
  const navigate = useNavigate();
  return useCallback(() => {
    if (stack.length >= 2) {
      const prev = stack[stack.length - 2];
      navigate(prev.pathname + prev.search);
    } else {
      navigate(fallback);
    }
  }, [stack, navigate, fallback]);
}
